import { fontFamilies, sizes, spacing, weights } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";
import {
  VictoryChart,
  VictoryAxis,
  VictoryLine,
  VictoryScatter,
  VictoryLegend,
} from "victory";

import { SectionMessage } from "../../section-message/SectionMessage";
import { Text } from "../../../foundations/typography/Text";

import { useColors } from "../../../hooks/useColors";

type Data = {
  x: number;
  y: number;
  xAxisLabel: string;
};

type XYGraphProps = {
  data: Data[];
  prefix?: string;
  testId?: string;
  chartTitle?: string;
};

export function XYGraph(props: XYGraphProps) {
  const { data = [], prefix = "$", testId = "chart", chartTitle } = props;

  const colors = useColors();
  const { t } = useTranslation();

  const xAxisLabels = data.map((item) => item.xAxisLabel);
  const yValues = data.map((item) => item.y);
  const domainLowestValue = Math.min(...yValues) * 0.75;
  const domainHighestValue = Math.max(...yValues) * 1.25;

  const noDatapoints = data.every((item) => item.y === 0);

  // Victory doesn't appear to get on well with the use of a styles object with CSS() like normal.
  const styles = {
    tickLabels: {
      padding: spacing["space-1"].px,
      fontSize: sizes["font-size-50"].px,
      fontFamily: fontFamilies["font-family-default"],
      fontWeight: weights["font-weight-regular"],
      fill: colors["text-weak"],
    },
    title: {
      fontSize: sizes["font-size-50"].px,
      fontFamily: fontFamilies["font-family-default"],
      fontWeight: weights["font-weight-medium"],
      fill: colors["text-base"],
    },
    line: { stroke: colors["icon-accent-blue"] },
    scatter: { fill: colors["icon-info"] },
    axis: { stroke: colors["text-weak"] },
  };

  if (!data.length || noDatapoints) {
    return (
      <SectionMessage
        type="danger"
        sectionTitle={t("No data")}
        children={
          <Text>{t("No data was provided to the Graph component.")}</Text>
        }
        icon="error"
      />
    );
  }

  return (
    <div className={style.container}>
      <VictoryChart
        aria-label="XY-Graph"
        data-testid={`${testId}`}
        maxDomain={{ y: domainHighestValue }}
        minDomain={{ y: domainLowestValue }}
        width={800}
        height={400}
      >
        {chartTitle && (
          <VictoryLegend
            title={chartTitle}
            style={{
              title: styles.title,
            }}
            data={[]}
          />
        )}
        <VictoryAxis
          tickValues={data.map((point) => point.x)}
          tickFormat={(x) =>
            xAxisLabels[data.findIndex((point) => point.x === x)]
          }
          style={{
            axis: styles.axis,
            tickLabels: styles.tickLabels,
          }}
        />
        <VictoryAxis
          dependentAxis
          tickFormat={(y) => `${prefix}${y}`}
          style={{
            axis: styles.axis,
            tickLabels: styles.tickLabels,
          }}
        />
        <VictoryScatter
          data={data}
          size={spacing["space-1"].px}
          style={{
            data: styles.scatter,
          }}
        />
        <VictoryLine
          interpolation="basis"
          style={{
            data: styles.line,
          }}
          data={data}
        />
      </VictoryChart>
    </div>
  );
}

const style = {
  container: css({
    display: "block",
  }),
};
